import cn from 'classnames';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { X, Building2 } from 'lucide-react';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { CustomerBo } from '@utils/types/back-office/customers';
import { BaseResponse } from '@utils/types/shared/base';
import { useDebouncedValue } from '@utils/hooks/use-debounced-value';
import { useNewAppSearchParams } from '@utils/hooks/use-new-app-search-params';
import { useThemeContext } from '@utils/hooks/use-theme-context';

type OrgOption = { id: string; label: string; email: string };

function useOrgSearch(q: string, enabled: boolean) {
  return useQuery<BaseResponse<CustomerBo[]>>(
    ['pq-org-search', q],
    async () => {
      const params = new URLSearchParams({ offset: '0', limit: '30' });
      if (q) {
        const isId = /^\d+$/.test(q.trim());
        params.append('q', isId ? `id:eq:${q.trim()}` : `name:ilike:${q}`);
      }
      const { data } = await Axios.get<BaseResponse<CustomerBo[]>>(
        `${URL_EXTENSION}/admin/customer?${params.toString()}`
      );
      return data;
    },
    { staleTime: 30_000, keepPreviousData: true, enabled }
  );
}

export function OrgQueueFilter() {
  const { t } = useTranslation();
  const { searchParams, setSearchParams } = useNewAppSearchParams();
  const { getColour, theme } = useThemeContext();

  const activeId = searchParams.organization_id
    ? Number(searchParams.organization_id)
    : undefined;
  const activeName = (searchParams.organization_name_label as string) || '';

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const debouncedQ = useDebouncedValue(input, 300);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading } = useOrgSearch(debouncedQ, isOpen);

  const options = useMemo<OrgOption[]>(
    () =>
      (data?.result ?? []).map(o => ({
        id: o.id,
        label: o.name,
        email: o.email
      })),
    [data]
  );

  const open = useCallback(() => {
    setIsOpen(true);
    setInput('');
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setInput('');
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) close();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, close]);

  const select = useCallback(
    (opt: OrgOption) => {
      setSearchParams({
        organization_id: String(opt.id),
        organization_name_label: opt.label,
        offset: '0'
      });
      close();
    },
    [setSearchParams, close]
  );

  const clear = useCallback(() => {
    setSearchParams({
      organization_id: undefined,
      organization_name_label: undefined,
      offset: '0'
    });
  }, [setSearchParams]);

  /* eslint-disable-next-line no-restricted-syntax -- Semantic accent colour, same as org-switcher */
  const borderColor = isOpen ? '#5794f6' : getColour('border');
  const textColor = getColour('normalText');
  const menuBg = getColour('secondLayer');
  const hoverBg =
    theme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

  const displayLabel = activeId
    ? activeName || `#${activeId}`
    : t('filter-by-org');

  return (
    <div ref={containerRef} className="relative flex items-center gap-1">
      <div
        className="flex items-center h-9 rounded-2xl border px-2.5 gap-1.5 cursor-pointer transition-colors min-w-[200px]"
        style={{ borderColor, backgroundColor: 'transparent' }}
        onClick={() => !isOpen && open()}
      >
        <Building2 className="h-3.5 w-3.5 shrink-0 text-blue-500" aria-hidden />
        {isOpen ? (
          <input
            ref={inputRef}
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === 'Escape' && close()}
            placeholder={displayLabel}
            className="flex-1 min-w-0 bg-transparent outline-none text-xs"
            style={{ color: textColor }}
          />
        ) : (
          <span
            className={cn(
              'flex-1 min-w-0 truncate text-xs select-none',
              activeId ? 'font-medium' : 'opacity-50'
            )}
            style={{ color: textColor }}
          >
            {displayLabel}
          </span>
        )}
      </div>

      {activeId && !isOpen && (
        <button
          type="button"
          onClick={clear}
          aria-label={t('clear-org-filter')}
          className="h-7 w-7 flex items-center justify-center rounded-2xl border transition hover:opacity-80"
          style={{ borderColor: getColour('border'), color: textColor }}
        >
          <X size={12} />
        </button>
      )}

      {isOpen && (
        <div
          className="absolute top-full mt-1 left-0 z-50 w-72 rounded-xl border shadow-xl overflow-hidden"
          style={{ backgroundColor: menuBg, borderColor: getColour('border') }}
        >
          <div className="max-h-64 overflow-y-auto py-1">
            {isLoading ? (
              <p
                className="px-3 py-3 text-xs text-center opacity-50"
                style={{ color: textColor }}
              >
                {'...'}
              </p>
            ) : options.length === 0 ? (
              <p
                className="px-3 py-3 text-xs text-center opacity-50"
                style={{ color: textColor }}
              >
                {t('no-results')}
              </p>
            ) : (
              options.map(opt => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => select(opt)}
                  className="w-full text-left px-3 py-2 flex flex-col gap-0.5 transition-colors"
                  style={{ color: textColor }}
                  onMouseEnter={e =>
                    ((e.currentTarget as HTMLElement).style.backgroundColor =
                      hoverBg)
                  }
                  onMouseLeave={e =>
                    ((e.currentTarget as HTMLElement).style.backgroundColor =
                      '')
                  }
                >
                  <span className="text-sm font-medium">
                    <span className="opacity-40 text-xs font-mono font-normal">
                      #{opt.id}{' '}
                    </span>
                    {opt.label}
                  </span>
                  {opt.email && (
                    <span className="text-xs opacity-50">{opt.email}</span>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
